/**
 * Parallax: a passive `scroll` listener on the page's own scroller schedules
 * at most one rAF per frame, which sets `--py` (-1..1, a section's own centre
 * relative to the viewport's) on every `[data-parallax]` section. The CSS
 * that reads `--py` lives beside each section in `sections/`; this
 * file only ever measures (`getBoundingClientRect`, read-only) and writes one
 * custom property, so it never triggers a layout of its own.
 *
 * Off entirely under reduced motion: `--py` is simply never set, and the CSS
 * that would key off it falls back to its default.
 */
import { onBeforeUnmount, onMounted } from 'vue';

function clamp(value: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, value));
}

export function useParallax(getRoot: () => HTMLElement | null): void {
  let root: HTMLElement | null = null;
  let sections: HTMLElement[] = [];
  let raf = 0;

  function update(): void {
    raf = 0;
    if (!root) return;
    const viewport = root.clientHeight;
    for (const el of sections) {
      const rect = el.getBoundingClientRect();
      const centre = rect.top + rect.height / 2 - viewport / 2;
      const py = clamp(centre / (viewport / 2 + rect.height / 2), -1, 1);
      el.style.setProperty('--py', py.toFixed(3));
    }
  }

  function onScroll(): void {
    if (raf) return;
    raf = requestAnimationFrame(update);
  }

  onMounted(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    root = getRoot();
    if (!root) return;
    sections = Array.from(root.querySelectorAll<HTMLElement>('[data-parallax]'));
    if (!sections.length) return;
    root.addEventListener('scroll', onScroll, { passive: true });
    update();
  });

  onBeforeUnmount(() => {
    root?.removeEventListener('scroll', onScroll);
    if (raf) cancelAnimationFrame(raf);
  });
}
